// 🎯 REPORTS: Auto-send report configuration and management
// Purpose: Manage automated report scheduling and recipient management
// Approach: Simple, user-friendly report automation

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ReportRecipient {
  id: string
  email: string
  name: string
  isActive: boolean
  addedAt: string
}

export interface ReportConfig {
  id: string
  name: string
  frequency: 'biweekly' | 'monthly' | 'bimonthly' | 'quarterly'
  scope: 'individual' | 'department' | 'company'
  targetUserId?: string // For individual reports
  departmentId?: string // For department reports
  recipients: ReportRecipient[]
  deliveryMethod: 'link' | 'pdf' | 'both'
  isActive: boolean
  nextSendDate: string | null
  lastSentDate: string | null
  reportUrl?: string // SSO link for the report
  createdAt: string
  updatedAt: string
}

export interface ReportsState {
  configs: ReportConfig[]
  isLoading: boolean
  error: string | null
  isModified: boolean
  lastSaved: string | null
}

const initialState: ReportsState = {
  configs: [],
  isLoading: false,
  error: null,
  isModified: false,
  lastSaved: null
}

// 🚀 UTILITIES: Helper functions
const generateId = () => `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const calculateNextSendDate = (frequency: ReportConfig['frequency']): string => {
  const now = new Date()
  const nextDate = new Date(now)
  
  switch (frequency) {
    case 'biweekly':
      nextDate.setDate(now.getDate() + 14)
      break
    case 'monthly':
      nextDate.setMonth(now.getMonth() + 1)
      break
    case 'bimonthly':
      nextDate.setMonth(now.getMonth() + 2)
      break
    case 'quarterly':
      nextDate.setMonth(now.getMonth() + 3)
      break
  }
  
  return nextDate.toISOString()
}

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    // 🚀 LOADING STATES
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    
    // 🎯 REPORT CONFIG MANAGEMENT
    createReportConfig: (state, action: PayloadAction<{
      name: string
      frequency: ReportConfig['frequency']
      scope: ReportConfig['scope']
      departmentId?: string
    }>) => {
      const now = new Date().toISOString()
      const newConfig: ReportConfig = {
        id: generateId(),
        name: action.payload.name,
        frequency: action.payload.frequency,
        scope: action.payload.scope,
        departmentId: action.payload.departmentId,
        recipients: [],
        deliveryMethod: 'link', // Default to link delivery
        isActive: true,
        nextSendDate: calculateNextSendDate(action.payload.frequency),
        lastSentDate: null,
        createdAt: now,
        updatedAt: now
      }
      
      state.configs.push(newConfig)
      state.isModified = true
      state.error = null
    },
    
    updateReportConfig: (state, action: PayloadAction<{
      id: string
      updates: Partial<Omit<ReportConfig, 'id' | 'createdAt' | 'recipients'>>
    }>) => {
      const { id, updates } = action.payload
      const configIndex = state.configs.findIndex(config => config.id === id)
      
      if (configIndex !== -1) {
        const currentConfig = state.configs[configIndex]
        
        // Recalculate next send date if frequency changed
        let nextSendDate = currentConfig.nextSendDate
        if (updates.frequency && updates.frequency !== currentConfig.frequency) {
          nextSendDate = calculateNextSendDate(updates.frequency)
        }
        
        state.configs[configIndex] = {
          ...currentConfig,
          ...updates,
          nextSendDate,
          updatedAt: new Date().toISOString()
        }
        
        state.isModified = true
        state.error = null
      }
    },
    
    deleteReportConfig: (state, action: PayloadAction<string>) => {
      state.configs = state.configs.filter(config => config.id !== action.payload)
      state.isModified = true
    },
    
    toggleReportConfig: (state, action: PayloadAction<string>) => {
      const configIndex = state.configs.findIndex(config => config.id === action.payload)
      
      if (configIndex !== -1) {
        state.configs[configIndex].isActive = !state.configs[configIndex].isActive
        state.configs[configIndex].updatedAt = new Date().toISOString()
        state.isModified = true
      }
    },
    
    // 🎯 RECIPIENT MANAGEMENT
    addRecipient: (state, action: PayloadAction<{
      configId: string
      email: string
      name: string
    }>) => {
      const { configId, email, name } = action.payload
      const configIndex = state.configs.findIndex(config => config.id === configId)
      
      if (configIndex !== -1) {
        // Check if email already exists
        const existingRecipient = state.configs[configIndex].recipients.find(
          recipient => recipient.email.toLowerCase() === email.toLowerCase()
        )
        
        if (!existingRecipient) {
          const newRecipient: ReportRecipient = {
            id: generateId(),
            email: email.toLowerCase().trim(),
            name: name.trim(),
            isActive: true,
            addedAt: new Date().toISOString()
          }
          
          state.configs[configIndex].recipients.push(newRecipient)
          state.configs[configIndex].updatedAt = new Date().toISOString()
          state.isModified = true
          state.error = null
        } else {
          state.error = 'This email address is already added to the report'
        }
      }
    },
    
    updateRecipient: (state, action: PayloadAction<{
      configId: string
      recipientId: string
      updates: Partial<Omit<ReportRecipient, 'id' | 'addedAt'>>
    }>) => {
      const { configId, recipientId, updates } = action.payload
      const configIndex = state.configs.findIndex(config => config.id === configId)
      
      if (configIndex !== -1) {
        const recipientIndex = state.configs[configIndex].recipients.findIndex(
          recipient => recipient.id === recipientId
        )
        
        if (recipientIndex !== -1) {
          state.configs[configIndex].recipients[recipientIndex] = {
            ...state.configs[configIndex].recipients[recipientIndex],
            ...updates
          }
          
          state.configs[configIndex].updatedAt = new Date().toISOString()
          state.isModified = true
        }
      }
    },
    
    removeRecipient: (state, action: PayloadAction<{
      configId: string
      recipientId: string
    }>) => {
      const { configId, recipientId } = action.payload
      const configIndex = state.configs.findIndex(config => config.id === configId)
      
      if (configIndex !== -1) {
        state.configs[configIndex].recipients = state.configs[configIndex].recipients.filter(
          recipient => recipient.id !== recipientId
        )
        
        state.configs[configIndex].updatedAt = new Date().toISOString()
        state.isModified = true
      }
    },
    
    toggleRecipient: (state, action: PayloadAction<{
      configId: string
      recipientId: string
    }>) => {
      const { configId, recipientId } = action.payload
      const configIndex = state.configs.findIndex(config => config.id === configId)
      
      if (configIndex !== -1) {
        const recipientIndex = state.configs[configIndex].recipients.findIndex(
          recipient => recipient.id === recipientId
        )
        
        if (recipientIndex !== -1) {
          state.configs[configIndex].recipients[recipientIndex].isActive = 
            !state.configs[configIndex].recipients[recipientIndex].isActive
          
          state.configs[configIndex].updatedAt = new Date().toISOString()
          state.isModified = true
        }
      }
    },
    
    // 🎯 BULK OPERATIONS
    saveReports: (state) => {
      // In a real app, this would trigger an API call
      state.isModified = false
      state.lastSaved = new Date().toISOString()
      state.error = null
    },
    
    loadReports: (state, action: PayloadAction<ReportConfig[]>) => {
      state.configs = action.payload
      state.isModified = false
      state.lastSaved = new Date().toISOString()
      state.error = null
    },
    
    // 🚀 REPORT SENDING (would trigger API in real app)
    markReportAsSent: (state, action: PayloadAction<string>) => {
      const configIndex = state.configs.findIndex(config => config.id === action.payload)
      
      if (configIndex !== -1) {
        const config = state.configs[configIndex]
        state.configs[configIndex].lastSentDate = new Date().toISOString()
        state.configs[configIndex].nextSendDate = calculateNextSendDate(config.frequency)
        state.configs[configIndex].updatedAt = new Date().toISOString()
      }
    },
    
    clearError: (state) => {
      state.error = null
    }
  }
})

export const {
  setLoading,
  setError,
  createReportConfig,
  updateReportConfig,
  deleteReportConfig,
  toggleReportConfig,
  addRecipient,
  updateRecipient,
  removeRecipient,
  toggleRecipient,
  saveReports,
  loadReports,
  markReportAsSent,
  clearError
} = reportsSlice.actions

export default reportsSlice.reducer

// 🎯 SELECTORS: Easy access to reports state
export const selectReportConfigs = (state: { reports: ReportsState }) => state.reports.configs
export const selectActiveReportConfigs = (state: { reports: ReportsState }) => 
  state.reports.configs.filter(config => config.isActive)
export const selectReportsLoading = (state: { reports: ReportsState }) => state.reports.isLoading
export const selectReportsError = (state: { reports: ReportsState }) => state.reports.error
export const selectIsReportsModified = (state: { reports: ReportsState }) => state.reports.isModified
export const selectReportsLastSaved = (state: { reports: ReportsState }) => state.reports.lastSaved

// 🚀 COMPUTED SELECTORS
export const selectReportConfigById = (configId: string) => 
  (state: { reports: ReportsState }) => 
    state.reports.configs.find(config => config.id === configId)

export const selectActiveRecipientsCount = (state: { reports: ReportsState }) => 
  state.reports.configs.reduce((total, config) => 
    total + config.recipients.filter(recipient => recipient.isActive).length, 0
  )

export const selectUpcomingReports = (state: { reports: ReportsState }) => 
  state.reports.configs
    .filter(config => config.isActive && config.nextSendDate)
    .sort((a, b) => 
      new Date(a.nextSendDate!).getTime() - new Date(b.nextSendDate!).getTime()
    )
    .slice(0, 5) // Next 5 upcoming reports

// 🎯 VALIDATORS: Report validation helpers
export const validateReportConfig = (config: Partial<ReportConfig>): string[] => {
  const errors: string[] = []
  
  if (!config.name || config.name.trim().length === 0) {
    errors.push('Report name is required')
  }
  
  if (!config.frequency) {
    errors.push('Frequency is required')
  }
  
  if (!config.scope) {
    errors.push('Report scope is required')
  }
  
  if (config.scope === 'department' && !config.departmentId) {
    errors.push('Department must be selected for department scope')
  }
  
  return errors
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}
